
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
   TrashIcon, AcademicCapIcon,
   PencilSquareIcon, DocumentPlusIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import adminApi from '../../api/adminApi';
import Toast from '../../components/ui/Toast';

const InternshipAssignmentManager = () => {
  const [internshipsList, setInternshipsList] = useState([]); 
  const [targetInternship, setTargetInternship] = useState(null);
  const [draftAssignments, setDraftAssignments] = useState([]);
  const [notification, setNotification] = useState(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(null);

  // Structured form state
  const [formData, setFormData] = useState({
    week_number: 1,
    title: '',
    objective: '',
    goal: '',
    tasks: '' 
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const data = await adminApi.getCompetitions(); 
      const onlyInternships = data.competitions.filter(
        item => item.category?.toLowerCase() === 'internship' || item.type?.toLowerCase() === 'internship'
      );
      setInternshipsList(onlyInternships || []);
    } catch (err) {
      setNotification({ msg: "Failed to load programs", type: 'error' });
    }
  };

  const handleSelectInternship = async (e) => {
    const selectedId = e.target.value;
    if (!selectedId) {
      setTargetInternship(null);
      setDraftAssignments([]);
      return;
    }

    const target = internshipsList.find(i => String(i.id) === String(selectedId));
    if (target) {
      setTargetInternship(target);
      setDraftAssignments([]); 
      try {
        const response = await adminApi.getInternshipTasks(target.id);
        const tasks = response?.tasks || response?.data || response || [];
        setDraftAssignments(tasks);
        setFormData(prev => ({ ...prev, week_number: (tasks.length || 0) + 1 }));
        setNotification({ msg: `Target Locked: ${target.title}`, type: 'success' });
      } catch (err) {
        setDraftAssignments([]);
        setFormData(prev => ({ ...prev, week_number: 1 }));
      }
    }
  };

  const addToDraft = (e) => {
    e.preventDefault();
    // Logic: Formats the separate fields into one clean string for the DB
    const formattedDetails = `OBJECTIVE: ${formData.objective}\nGOAL: ${formData.goal}\n${formData.tasks}`;
    
    const newDraft = { 
        ...formData, 
        task_details: formattedDetails, 
        id: `temp-${Date.now()}` 
    };

    const updated = [...draftAssignments, newDraft].sort((a, b) => a.week_number - b.week_number);
    setDraftAssignments(updated);
    
    setFormData({ 
      week_number: updated.length + 1, 
      title: '', objective: '', goal: '', tasks: '' 
    });
  };

  const handleRemoveWeek = async (weekNum, id) => {
    if (String(id).startsWith('temp-')) {
      setDraftAssignments(prev => prev.filter(a => a.id !== id));
      return;
    }

    if (window.confirm(`Permanently delete Week ${weekNum} from the database?`)) {
      try {
        await adminApi.deleteSpecificWeek(targetInternship.id, weekNum);
        setDraftAssignments(prev => prev.filter(a => a.week_number !== weekNum));
        setNotification({ msg: "Removed from server", type: 'success' });
      } catch (err) {
        setNotification({ msg: "Delete failed", type: 'error' });
      }
    }
  };

  const deploySyllabus = async () => {
    if (!targetInternship || draftAssignments.length === 0) return;
    setIsDeploying(true);
    try {
      await adminApi.deployInternshipAssignment({
        internship_id: targetInternship.id,
        assignments: draftAssignments
      }); 
      setNotification({ msg: "Syllabus Deployed!", type: 'success' });
    } catch (err) {
      setNotification({ msg: "Deployment failed", type: 'error' });
    } finally {
      setIsDeploying(false);
    }
  };

  const requiredWeeks = targetInternship ? (targetInternship.duration_months * 4) : 0;
  const progressPercent = targetInternship ? (draftAssignments.length / requiredWeeks) * 100 : 0;

  return (
    <div className="space-y-6">
      {notification && <Toast message={notification.msg} type={notification.type} onClose={() => setNotification(null)} />}

      {/* TOP NAV BAR */}
      <div className="bg-gray-800/50 p-6 rounded-2xl border border-gray-700 grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <AcademicCapIcon className="h-8 w-8 text-emerald-500" /> Assignment Master
          </h1>
          <p className="text-gray-400 text-sm italic">
            {targetInternship ? `Program: ${targetInternship.title}` : 'Awaiting Selection...'}
          </p>
        </div>
        
        <div className="relative">
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Choose Internship</label>
          <div className="relative mt-1">
            <select 
                onChange={handleSelectInternship}
                className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-2.5 text-white focus:border-emerald-500 outline-none transition-all appearance-none cursor-pointer"
                value={targetInternship?.id || ""}
            >
                <option value="">-- Select Internship --</option>
                {internshipsList.map(item => (
                    <option key={item.id} value={item.id}>{item.title}</option>
                ))}
            </select>
            <ChevronDownIcon className="h-5 w-5 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Syllabus Progress: {requiredWeeks} Weeks</label>
          <div className="mt-1 bg-gray-900 border border-gray-700 rounded-xl px-4 py-2">
            <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald-500 font-bold">{draftAssignments.length} Drafted</span>
                <span className="text-gray-500">{Math.round(progressPercent)}%</span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-1.5">
                <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(progressPercent, 100)}%` }} className="bg-emerald-500 h-full rounded-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* FORM SIDE */}
        <form onSubmit={addToDraft} className="bg-gray-900/60 p-8 rounded-[2.5rem] border border-gray-700/50 space-y-4 h-fit sticky top-6">
          <div className="flex justify-between items-center border-b border-gray-800 pb-4">
            <h3 className="text-lg font-bold text-emerald-400">Mission Architect</h3>
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Week:</span>
                <input type="number" className="w-16 bg-gray-950 border border-gray-700 rounded-lg px-2 py-1 text-white text-center" value={formData.week_number} onChange={(e) => setFormData({...formData, week_number: parseInt(e.target.value)})}/>
            </div>
          </div>

          <div className="space-y-4">
            <input className="w-full bg-gray-950 border border-gray-800 rounded-xl p-3 text-white outline-none focus:border-emerald-500" required placeholder="Week Title (e.g. React Fundamentals)" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})}/>
            
            <input className="w-full bg-gray-950 border border-gray-800 rounded-xl p-3 text-white outline-none focus:border-indigo-500" required placeholder="Mission Objective..." value={formData.objective} onChange={(e) => setFormData({...formData, objective: e.target.value})}/>
            
            <input className="w-full bg-gray-950 border border-gray-800 rounded-xl p-3 text-white outline-none focus:border-indigo-500" required placeholder="Ultimate Goal..." value={formData.goal} onChange={(e) => setFormData({...formData, goal: e.target.value})}/>

            <textarea className="w-full bg-gray-950 border border-gray-800 rounded-2xl p-4 text-white outline-none focus:border-emerald-500" rows="4" required placeholder="Technical Tasks (One per line)&#10;• Task 1&#10;• Task 2" value={formData.tasks} onChange={(e) => setFormData({...formData, tasks: e.target.value})}/>
          </div>

          <button type="submit" disabled={!targetInternship} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-2xl transition-all shadow-lg shadow-emerald-900/20">
            Add to Syllabus
          </button>
        </form>

        {/* PREVIEW & DELETE SIDE */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-lg font-bold text-gray-300">Syllabus Preview</h3>
            <button onClick={deploySyllabus} disabled={isDeploying || draftAssignments.length === 0} className="px-6 py-2 rounded-xl bg-emerald-600 text-white font-bold hover:scale-105 transition-all">
              {isDeploying ? 'Deploying...' : 'SAVE & DEPLOY'}
            </button>

            <button
  onClick={async () => {
    if (!targetInternship) return;

    const confirmDelete = window.confirm(
      `Type DELETE to permanently remove all weeks for ${targetInternship.title}`
    );

    if (!confirmDelete) return;

    try {
      await adminApi.deleteInternshipAssignment(targetInternship.id);
      setDraftAssignments([]);
      setNotification({ msg: "Entire syllabus removed.", type: 'success' });
    } catch (err) {
      setNotification({ msg: "Delete failed", type: 'error' });
    }
  }}
  className="px-4 py-2 bg-red-600 text-white rounded-xl"
>
  DELETE ALL
</button>
          </div>

          <div className="bg-gray-900/40 border border-gray-700/50 rounded-3xl p-4 max-h-[600px] overflow-y-auto space-y-4 custom-scrollbar">
            {draftAssignments.length === 0 ? (
              <div className="py-20 text-center text-gray-600">
                <DocumentPlusIcon className="h-12 w-12 mx-auto opacity-20" />
                <p className="mt-2 font-mono text-sm">// Terminal Empty</p>
              </div>
            ) : (
              draftAssignments.map((assignment) => (
                <motion.div layout key={assignment.id} className="bg-gray-800/40 p-5 rounded-2xl border border-gray-700 group relative">
                  <div className="flex gap-4">
                    <div className="text-emerald-500 font-black text-xs mt-1">W{assignment.week_number}</div>
                    <div className="flex-1">
                      <h4 className="text-white font-bold text-sm uppercase tracking-tight">{assignment.title}</h4>
                      <p 
  onClick={() => setExpandedWeek(
    expandedWeek === assignment.week_number ? null : assignment.week_number
  )}
  className={`text-gray-500 text-[10px] mt-1 italic cursor-pointer ${
    expandedWeek === assignment.week_number ? '' : 'line-clamp-2'
  }`}
>
  {assignment.task_details}
</p>
                    </div>
                  </div>
                  
                  {/* RESTORED CONTROL BUTTONS */}
                  <div className="absolute top-4 right-4 flex gap-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      type="button"
                      onClick={() => {
                        // Logic to load back into form for editing
                        setFormData({
                            week_number: assignment.week_number,
                            title: assignment.title,
                            objective: '', // Since these are joined in DB, user can manually edit the details box
                            goal: '',
                            tasks: assignment.task_details
                        });
                        setDraftAssignments(prev => prev.filter(a => a.id !== assignment.id));
                      }} 
                      className="text-gray-400 hover:text-emerald-400 transition-colors"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button 
                      type="button"
                      onClick={() => handleRemoveWeek(assignment.week_number, assignment.id)} 
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternshipAssignmentManager;